import React, { useEffect, useState } from 'react';
import { Avatar, Badge, Calendar, CalendarProps, message, Popconfirm, Spin, Tooltip } from 'components/shared/AntComponent';
import dayjs, { Dayjs } from 'dayjs';
import { Form, Input, DatePicker, Select } from 'components/shared/FormComponent';
import { Button } from 'components/shared/ButtonComponent';
import { LeftOutlined, RightOutlined, DeleteFilled } from 'components/shared/AntIcons';
import 'dayjs/locale/en';
import updateLocale from 'dayjs/plugin/updateLocale';
import { useDispatch, useSelector } from 'react-redux';
import { deleteEvent, getEvent, saveEvent } from 'services/actions/eventServiceAction';

dayjs.extend(updateLocale);

// Update locale to use 3-letter weekday labels
dayjs.updateLocale('en', {
    weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
});
const { TextArea } = Input;
type EventItem = {
    date: string; // in 'YYYY-MM-DD' format
    title: string;
    status: string;
    description: string;
};

const EventManagement: React.FC = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const { eventInfo, loading1, success1, error1, loading, loading2, success2, error2 } = useSelector((state: any) => state.event);
    const [events, setEvents] = useState<EventItem[]>([]);
    const [value, setValue] = useState<Dayjs>(dayjs());
    const [openEvent, setOpenEvent] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [rowData, setRowData]: any = useState(null);
    const [showSuccessmsg, setShowSuccessmsg] = useState(false);
    const successmsg = showSuccessmsg ? success1 : false;
    const [showErrormsg, setShowErrormsg] = useState(false);
    const errormsg = showErrormsg ? error1 : false;
    const [showSuccessmsg1, setShowSuccessmsg1] = useState(false);
    const successmsg1 = showSuccessmsg1 ? success2 : false;
    const [showErrormsg1, setShowErrormsg1] = useState(false);
    const errormsg1 = showErrormsg1 ? error2 : false;
    const [isPast, setIsPast] = useState(false);
    const tagColors = ['#ffd9dc', '#dedef9', '#dcf0fd', '#fbf1d7'];
    const eventDate = Form.useWatch("eventDate", form);
    const isPastDate = (date) =>
        date && dayjs(date).isBefore(dayjs(), "day");

    const statusOptions = [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
        { label: "Closed", value: "closed" },
    ];
    // console.log("events", events, eventInfo);
    useEffect(() => {
        if (!isPastDate(eventDate)) {
            const status = form.getFieldValue("status");
            if (status === "closed") {
                form.setFieldsValue({ status: "active" });
            }
        }
    }, [eventDate]);
    useEffect(() => {
        if (eventInfo?.data) {
            const formattedEvents = eventInfo?.data.map((event: any) => ({
                ...event,
                event_date: dayjs(new Date(event?.event_date)).format('YYYY-MM-DD')  // Change format here
            }));
            setEvents(eventInfo?.data);
        }
    }, [eventInfo]);
    const getStatusOptions = (eventDate) => {
        return isPastDate(eventDate)
            ? statusOptions
            : statusOptions.filter(opt => opt.value !== "closed");
    };


    const handleShowEvent = () => {
        setOpenEvent(true);
        setIsPast(false);
        setRowData(null);
        form.resetFields();
    }
    const handleCloseEvent = () => {
        setOpenEvent(false);
        setIsNew(false);
        setIsPast(false);
        setRowData(null);
    }

    const onPanelChange: CalendarProps<Dayjs>['onPanelChange'] = (newValue) => {
        setValue(newValue);
    };
    const getEventDetails = () => {
        const inputJson = {}
        dispatch(getEvent(inputJson) as any);
    }

    useEffect(() => {
        getEventDetails();
    }, []);

    const headerRender: CalendarProps<Dayjs>['headerRender'] = ({
        value: current,
        onChange,
    }) => {
        const prevMonth = (e: any) => {
            e.stopPropagation()
            const newDate = current.subtract(1, 'month');
            setValue(newDate);
            onChange(newDate);
            setOpenEvent(false);
        };

        const nextMonth = (e: any) => {
            e.stopPropagation()
            const newDate = current.add(1, 'month');
            setValue(newDate);
            onChange(newDate);
            setOpenEvent(false);
        };
        return (
            <div className='row mx-0 justify-content-center align-items-center py-3 px-0 bg-light'>
                <div className='col-md-auto col-sm-auto text-end'>
                    <Button
                        type="text"
                        className="bg-light shadow-sm"
                        icon={<LeftOutlined />}
                        onClick={(e) => prevMonth(e)}
                    />
                </div>
                <div className='col-md-3 d-flex align-items-center justify-content-center'>
                    {dayjs().isSame(current, 'month') && (
                        <div className="border rounded me-2">
                            <div className="bg-light px-2 py-0 pb-1 text-center fs-14 text-uppercase">
                                {dayjs().format('ddd')}{/* {current.format('MMM')} SEP */}
                            </div>
                            <div className='bg-white px-2 py-0 text-center fw-bold fs-15'>
                                {dayjs().format('DD')}  {/* 02 */}
                            </div>
                        </div>)}
                    <div className=' my-auto'>
                        <h5 className='text-primary mb-1'>{current.format('MMMM YYYY')}</h5>
                        <h6 className='text-secondary fs-14 fw-normal ps-0'>
                            {current.startOf('month').format('MMM D, YYYY')} - {current.endOf('month').format('MMM D, YYYY')}

                            {/* Sep 1, 2025 - Sep 31, 2025 */}
                        </h6>
                    </div>
                </div>
                <div className='col-md-auto col-sm-auto me-auto'>
                    <Button
                        type="text"
                        className="bg-light shadow-sm"
                        icon={<RightOutlined />}
                        onClick={(e) => nextMonth(e)}
                    />
                </div>
                <div className='col-auto ms-auto pe-0'>
                    <Button type="primary" onClick={handleShowEvent}>Create Event</Button>
                </div>
            </div>
        );
    };

    const handleEditForm = (values: any) => {
        setIsNew(true);
        const formattedEventDate = values?.event_date
            ? dayjs(values.event_date).isValid()
                ? dayjs(values.event_date).format('YYYY-MM-DD')
                : null
            : null;

        if (values) {
            const eventDate = dayjs(formattedEventDate);
            const today = dayjs().startOf('day'); // normalize to date only
            form.setFieldsValue({
                "eventDate": formattedEventDate ? dayjs(formattedEventDate) : "",
                "description": values?.description,
                "status": values?.event_status,
                "eventName": values?.event_name
            });
            if (eventDate.isBefore(today)) {
                setIsPast(true);
            } else {
                setIsPast(false);
            }

        } else {
            form.setFieldsValue({
                "eventDate": dayjs(value),
                "description": "",
                "status": "",
                "eventName": ""
            });
            setIsPast(false);
        }
        onSelect(value);

        setOpenEvent(true);

    }
    // console.log("rwData", rowData);
    useEffect(() => {
        if (isNew) {
            handleEditForm(rowData);
        }
    }, [isNew]);
    const onSelect = (date: Dayjs) => {
        if (isNew) return;
        if (!isNew) {
            form.setFieldsValue({
                "eventDate": dayjs(date),
                "description": "",
                "status": "",
                "eventName": ""
            });
            setOpenEvent(true);
            // console.log("date", date);
        }

        // Only trigger if clicked date is within the currently displayed month
        if (date.month() !== value.month() || date.year() !== value.year()) {
            setValue(date); // just update value to allow navigation
            return;
        }


    };
    const submitForm = (values: any) => {
        // console.log("values", values);
        const inputJson = {
            "id": rowData?.id || 0,
            "event_name": values.eventName?.trim() || "",
            "event_date": values?.eventDate ? dayjs(values?.eventDate)?.format('YYYY-MM-DD') : null,
            "event_status": values?.status || "Active",
            "description": values?.description || "this site under development"
        }
        // console.log("inptuJson", inputJson);
        dispatch(saveEvent(inputJson) as any);
        setShowErrormsg(true);
        setShowSuccessmsg(true);
    }

    useEffect(() => {
        if (successmsg) {
            message.success('Event saved successfully');
            setShowSuccessmsg(false);
            getEventDetails();
            setOpenEvent(false);
        }
        if (errormsg) {
            message.error("Event couldn't be saved");
            setShowErrormsg(false);
        }
    }, [successmsg, errormsg]);

    const disableDates = (current) => {
        return current && current < dayjs().startOf('day');
    };

    const confirmDelete = () => {
        dispatch(deleteEvent(rowData?.id) as any);
        setShowSuccessmsg1(true);
        setShowErrormsg1(true);
    }

    useEffect(() => {
        if (successmsg1) {
            setShowSuccessmsg1(false);
            setOpenEvent(false);
            getEventDetails();
            message.success('Event deleted successfully');
        }
        if (errormsg1) {
            setShowErrormsg1(false);
            if (error2?.data) {
                message.error(error2?.data);
            } else {
                message.error("Event couldn't be deleted");
            }

        }
    }, [successmsg1, errormsg1]);
    const disablePastDates = (current) => {
        return current && current.isBefore(dayjs().startOf("day"));
    };
    return (
        <div className="p-2">
            <div className="d-flex grid-title-card mb-3">
                <h5 className="my-auto ">Event Management</h5>
            </div>
            <div className='row mx-0 h-100'>
                <div className='col bg-white p-2 full-calendar me-3 h-100'>
                    {openEvent ? <div>
                        <div className="d-flex w-100 pb-3 pt-2 border-bottom">
                            <h6 className="text-primary ps-2 ms-1 fs-17  my-auto" >{rowData ? "Update Event" : "Create Event"}</h6>
                            {rowData ? <div className='ms-auto text-danger'>
                                <Popconfirm placement="topLeft"
                                    title="Are you sure to delete this event?"
                                    description="Delete the event"
                                    onConfirm={() => {
                                        confirmDelete();
                                        setShowSuccessmsg1(true);
                                        setShowErrormsg1(true);
                                    }}
                                    okText="Yes"
                                    cancelText="No">
                                    <Tooltip title="Delete Event">
                                        <DeleteFilled />
                                    </Tooltip>
                                </Popconfirm>
                            </div> : ""}
                        </div>
                        <Spin spinning={loading2} className='text-danger' tip="Deleting...">
                            <Form form={form} layout='vertical'
                                onFinish={submitForm}>
                                <div className='row mx-0 mt-3'>
                                    <Form.Item name="eventName" label="Event Name" className='col-md-7'
                                        rules={[{ required: true, message: 'This field is required' }]}>
                                        <Input disabled={isPast} />
                                    </Form.Item>
                                    <Form.Item name="eventDate" label="Event Date" className='col'
                                        rules={[{ required: true, message: 'This field is required' }]}>
                                        <DatePicker className="w-100" format="MM-DD-YYYY" disabled={isPast} disabledDate={disableDates} />
                                    </Form.Item>
                                    <Form.Item name="status" label="Event Status" className='col'
                                        rules={[{ required: true, message: 'This field is required' }]}>
                                        <Select options={getStatusOptions(eventDate)} disabled={isPast} />
                                    </Form.Item>
                                </div>
                                <div className='row mx-0'>
                                    <Form.Item name="description" label="Description" className='col-md-12'
                                    // rules={[{ required: true, message: 'This field is required' }]}
                                    >
                                        <TextArea rows={2} disabled={isPast} />
                                    </Form.Item>
                                </div>
                                <div className=' pe-2 me-1 mb-2 d-flex'>
                                    <Button type="default" className="bg-danger text-white me-2 ms-auto border-0" onClick={handleCloseEvent}>
                                        Cancel
                                    </Button>
                                    <Button type="primary" htmlType='submit' loading={loading1} disabled={isPast}>
                                        Save
                                    </Button>
                                </div>
                            </Form>
                        </Spin>
                    </div> :
                        <Spin spinning={loading}>
                            <Calendar fullscreen={true} value={value}
                                onPanelChange={onPanelChange}
                                headerRender={headerRender}
                                onSelect={onSelect}
                                disabledDate={disablePastDates}
                                onChange={() => { setRowData(null); }}
                                cellRender={(current, info) => {
                                    if (info.type !== 'date') return null;
                                    const formatted = current.format('YYYY-MM-DD');
                                    const eventsForDate = events.filter((ev: any) => dayjs(ev?.event_date).format('YYYY-MM-DD') === formatted);
                                    return (
                                        <div>
                                            <div className="events">
                                                {eventsForDate?.map((item: any, index: number) => (
                                                    <div key={index} onClick={(event: any) => { event.stopPropagation(); handleEditForm(value); setIsNew(true); setRowData(item); }} className='bg-aliceblue px-2 py-1 rounded my-2'>
                                                        <Badge status={item.event_status?.toLowerCase() == "active" ? 'success' : item.event_status?.toLowerCase() == 'closed' || item.event_status?.toLowerCase() == 'close' ? 'error' : 'warning'} text={item.event_name} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }} />
                        </Spin>}
                </div>
                <div className='col-md-3 bg-white px-0 shadow-sm overflow-auto'>
                    <div className="d-flex border-bottom p-2 error-title-card px-3 py-2">
                        <h5 className="txt-primary p-2 fs-17 my-auto">Event List</h5>
                    </div>
                    <div className="notification-container noti-cont credit-notify overflow-auto">
                        {events?.length > 0 ? <div>
                            {events?.map((item: any, index: number) => {
                                return (
                                    <div key={index} className="d-flex w-100 px-3 py-2 border-bottom">
                                        <div className='col-auto'>
                                            <Avatar
                                                className="text-capitalize fw-bold"
                                                size={40}
                                                style={{ backgroundColor: `${tagColors[index % tagColors.length]}`, color: '#888888' }}
                                            >
                                                {item.event_name[0]}
                                            </Avatar>
                                        </div>
                                        <div className='col ps-2 my-auto'>
                                            <h6 className="mb-0">{item.event_name}</h6>
                                            <p className='mb-0'>{item.description}</p>
                                            <span className='fs-12 ps-0 text-gray'>{item.event_date ? dayjs(new Date(item?.event_date)).format("MM/DD/YYYY") : ""}</span>
                                        </div>
                                        <div className={`col-auto text-capitalize my-auto fw-bold bg-none
                                             ${item.event_status?.toLowerCase() === 'active' ? 'text-success' : item.event_status?.toLowerCase() === "closed" ? 'text-danger'
                                                : 'text-warning'}`}>{item.event_status}</div>
                                    </div>
                                )
                            })}
                        </div> : <div className="text-center p-4">No event available</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventManagement;
